import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './Button'
import { normaliseLanguage } from '../utils/downloadParticipantReport'

// Emoji flags (🇬🇧/🇯🇵) rely on the OS having a color-emoji font that supports
// regional-indicator flag ligatures — many Linux setups (and older Windows) lack one and
// fall back to rendering the two bare letters (e.g. "JP") instead of a flag. Inline SVGs
// render identically on every platform, so we use those instead.
const FlagGB = () => (
  <svg viewBox="0 0 20 15" className="h-3.5 w-5 shrink-0 rounded-[2px]" aria-hidden="true">
    <rect width="20" height="15" fill="#00247d" />
    <path d="M0 0 20 15M20 0 0 15" stroke="#fff" strokeWidth="3" />
    <path d="M0 0 20 15M20 0 0 15" stroke="#cf142b" strokeWidth="1" />
    <path d="M10 0V15M0 7.5H20" stroke="#fff" strokeWidth="5" />
    <path d="M10 0V15M0 7.5H20" stroke="#cf142b" strokeWidth="3" />
  </svg>
)

const FlagJP = () => (
  <svg viewBox="0 0 20 15" className="h-3.5 w-5 shrink-0 rounded-[2px]" aria-hidden="true">
    {/* White flag needs its own border since it would otherwise blend into the white
        button/dropdown background it sits on. */}
    <rect x="0.5" y="0.5" width="19" height="14" fill="#fff" stroke="#d1d5db" strokeWidth="1" />
    <circle cx="10" cy="7.5" r="4.2" fill="#bc002d" />
  </svg>
)

const LANGUAGES = [
  { code: 'en', label: 'English', Flag: FlagGB },
  { code: 'ja', label: '日本語', Flag: FlagJP },
] as const

interface LanguageSwitcherProps {
  /**
   * Which edge of the trigger button the dropdown menu hangs from. "left" (default) grows the
   * menu rightward — fits placements with room to the right, like the app Header (trigger sits
   * near the left edge). "right" grows it leftward instead — use this wherever the trigger is
   * pinned near the right edge of the viewport (e.g. a `fixed top-4 right-4` corner control on a
   * public page), otherwise the menu overflows/gets clipped off-screen on narrow viewports.
   */
  align?: 'left' | 'right'
}

/** Flag-icon button that opens a dropdown to switch between English and Japanese. */
export const LanguageSwitcher = ({ align = 'left' }: LanguageSwitcherProps) => {
  const { i18n } = useTranslation()
  const active = normaliseLanguage(i18n.language ?? 'en')
  const activeLanguage = LANGUAGES.find((l) => l.code === active) ?? LANGUAGES[0]
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [open])

  const select = (code: string) => {
    i18n.changeLanguage(code)
    setOpen(false)
  }

  return (
    <div className="relative inline-flex" ref={containerRef}>
      <Button
        type="button"
        variant="unstyled"
        className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
        aria-label="Change language"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <activeLanguage.Flag />
        <svg
          className={`h-2.5 w-2.5 opacity-50 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 10 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M2 3.5 5 6.5 8 3.5" />
        </svg>
      </Button>

      {open && (
        <ul
          className={`absolute top-full z-50 mt-1.5 min-w-[130px] list-none rounded-xl border border-gray-200 bg-white p-1 shadow-lg ${align === 'right' ? 'right-0' : 'left-0'}`}
          role="listbox"
          aria-label="Language"
        >
          {LANGUAGES.map(({ code, label, Flag }) => (
            <li key={code} role="option" aria-selected={active === code}>
              <Button
                type="button"
                variant="unstyled"
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors hover:bg-gray-50 ${active === code ? 'font-semibold text-blue-600' : 'text-gray-700'}`}
                onClick={() => select(code)}
              >
                <Flag />
                <span>{label}</span>
                {active === code && (
                  <svg
                    className="ml-auto h-3 w-3 text-blue-600"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M2 6l3 3 5-5" />
                  </svg>
                )}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
